import { buildPrompt } from '@/ai/utils/promptBuilder'
import { examples } from '@/ai/prompts/common/examples'
import { readModelNotes } from '@/ai/prompts/common/instructions'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { getAggregateName, getCommandName } from '@/ai/utils/helpers'
import { getJsonOutputFormatting } from '@/ai/prompts/common/formatting'
import {
  createSystemMessage,
  createUserMessage,
  diagramToJson,
  getWorkflowBackgroundSections,
  getEventsDataModelsBackgroundSections,
  getRoleCommandAggregateBackground
} from '@/ai/utils/promptUtils'

export const buildReadModelFromWorkflowPrompt = ({ eventId, workflow, svgGraph, svgLanes, cardTypes }) => {
  const messages = []

  messages.push(createSystemMessage(systemMessages.EVENT_MODELING_DDD_EXPERT))

  const promptSections = []

  const { workflow: currentDiagram, currentStep } = workflow.eventsJson?.length
    ? diagramToJson(eventId, workflow, svgGraph, svgLanes)
    : { workflow: [], currentStep: undefined }
  const currentStepObject = currentDiagram[currentStep - 1]
  const roleName = currentStepObject.role
  const description = currentStepObject.description
  const workflowBackgroundSections = getWorkflowBackgroundSections(workflow, currentDiagram, cardTypes)

  const currentEvent = svgGraph[eventId]
  const commandName = getCommandName(currentStepObject, cardTypes)
  const aggregateName = getAggregateName(currentStepObject, cardTypes)

  promptSections.push(...workflowBackgroundSections)

  promptSections.push({
    title: 'Background Actor, Command, Aggregate',
    content: getRoleCommandAggregateBackground({
      currentStep,
      roleName,
      description,
      commandName,
      aggregateName
    })
  })

  let dataModelBackgroundSections

  if (currentEvent) {
    dataModelBackgroundSections = getEventsDataModelsBackgroundSections([currentEvent], currentDiagram, cardTypes)
    promptSections.push(...dataModelBackgroundSections)
  }

  const readModelRequestContent = commandName && aggregateName
    ? `Provide the Read Model as a set of attributes (or data fields) to be displayed to
       the actor BEFORE invoking the command "${commandName}" on the aggregate ${aggregateName}.`
    : `Provide the Read Model as a set of attributes (or data fields) to be displayed to
       the actor BEFORE invoking the command in step ${currentStep}: ${roleName} - ${description}.`

  promptSections.push({
    title: 'Request',
    content: `
      Generate a Read Model (or Query Model) for the actor (human or system) about to make the necessary
      decisions to take action and complete step ${currentStep} (${roleName} - ${description}). The purpose of the
      Read Model (or Query Model) is to define a query (for one or many items) that displays the current system
      state in a way that is relevant to the actor about to do the task at hand. ${readModelRequestContent}
      Usually, between 2 and 8 attributes or fields are sufficient, but it can be more or less.
      An attribute can be a primitive or a reference to a non-primitive type like an associated entity. An attribute
      or field name must be at most 30 characters long.
    `
  })

  const readModelExample = { readModel: examples.readModelExampleOrder }

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: getJsonOutputFormatting('readModel', JSON.stringify(readModelExample))
  })

  const notes = [readModelNotes[0], readModelNotes[1]]

  if (dataModelBackgroundSections?.length) {
    notes.push(readModelNotes[2], readModelNotes[3])
  }

  promptSections.push({
    title: 'Note',
    contentType: 'bullet',
    content: notes
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return messages
}
